import React, { useEffect, useMemo, useState } from 'react';
import type { GroundRow } from './GroundsStep';
import '../../../leaguesHome/tournaments/tournamentsDetails/TournamentAboutTab.scss';
import './LeaguePreviewStep.scss';

export interface LeaguePreviewData {
  leagueName: string;
  organizerName: string;
  organizerEmailId: string;
  organizerPhoneNumber: string;
  countryCode: string;
  city: string;
  state: string;
  country: string;
  association: string;
  logoFile: File | null;
  bannerFile: File | null;
}

interface LeaguePreviewStepProps {
  data: LeaguePreviewData;
  grounds: GroundRow[];
  onPrevious: () => void;
  onCreate: () => Promise<void> | void;
  isSubmitting?: boolean;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="about-row">
    <span className="about-row__label">{label}</span>
    <span className="about-row__value">{value || '—'}</span>
  </div>
);

const useObjectUrl = (file: File | null): string | null => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const obj = URL.createObjectURL(file);
    setUrl(obj);
    return () => URL.revokeObjectURL(obj);
  }, [file]);
  return url;
};

const formatPhone = (phone: string, code: string): string => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  const cc = code.replace(/^\+/, '');
  if (cc && digits.startsWith(cc)) {
    return `+${cc} ${digits.slice(cc.length)}`;
  }
  return cc ? `+${cc} ${digits}` : digits;
};

const buildDaysLabel = (days: GroundRow['daysOpen']): string => {
  if (!days || days.length === 0) return '—';
  if (days.length === 7) return 'Everyday';
  return days.join(', ');
};

const buildHoursLabel = (from: string, to: string): string => {
  const f = from?.trim();
  const t = to?.trim();
  if (!f && !t) return '—';
  if (f && t) return `${f} – ${t}`;
  return f || t || '—';
};

const LeaguePreviewStep: React.FC<LeaguePreviewStepProps> = ({
  data,
  grounds,
  onPrevious,
  onCreate,
  isSubmitting,
}) => {
  const bannerUrl = useObjectUrl(data.bannerFile);
  const logoUrl = useObjectUrl(data.logoFile);

  const locationParts = [data.city, data.state, data.country].filter(Boolean);
  const locationLabel = locationParts.length > 0 ? locationParts.join(', ') : null;

  const filledGrounds = useMemo(
    () =>
      grounds.filter(
        (g) =>
          g.groundName.trim() ||
          g.groundAddress.trim() ||
          g.email.trim() ||
          g.contactNumber.trim() ||
          g.daysOpen.length > 0 ||
          g.fromTime.trim() ||
          g.toTime.trim()
      ),
    [grounds]
  );

  return (
    <div className="create-league-step-form league-preview-step">
      <h2 className="create-league-step-title">Review</h2>

      <div className="about-tab league-about-tab league-preview-about">
        {/* Hero */}
        <section className="about-card about-card--hero">
          <div className="about-hero-banner">
            {bannerUrl ? (
              <img
                className="about-hero-banner__img"
                src={bannerUrl}
                alt={data.leagueName || 'League banner'}
              />
            ) : (
              <div className="about-hero-banner__placeholder" aria-hidden />
            )}
            <div className="about-hero-scrim" aria-hidden />
            <div className="about-hero-overlay about-hero-overlay--league">
              <div className="about-hero-logo">
                {logoUrl ? (
                  <img src={logoUrl} alt={`${data.leagueName || 'League'} logo`} />
                ) : (
                  <div className="league-preview-logo-placeholder" aria-hidden />
                )}
              </div>
              <div className="about-hero-text">
                <div className="about-hero-meta-top">
                  {data.association && (
                    <span className="about-hero-tag">{data.association}</span>
                  )}
                </div>
                <h2 className="about-hero-title">{data.leagueName || 'Untitled League'}</h2>
                {locationLabel && (
                  <div className="about-hero-dates">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span>{locationLabel}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="about-grid">
          <section className="about-card">
            <h3 className="about-card__title">Organizer</h3>
            <div className="about-card__body">
              <Row label="Name" value={data.organizerName} />
              <Row label="Email" value={data.organizerEmailId} />
              <Row label="Phone" value={formatPhone(data.organizerPhoneNumber, data.countryCode)} />
            </div>
          </section>

          <section className="about-card">
            <h3 className="about-card__title">Location</h3>
            <div className="about-card__body">
              <Row label="City" value={data.city} />
              <Row label="State" value={data.state} />
              <Row label="Country" value={data.country} />
            </div>
          </section>

          <section className="about-card">
            <h3 className="about-card__title">Association</h3>
            <div className="about-card__body">
              <Row label="Association" value={data.association} />
            </div>
          </section>
        </div>

        <section className="about-card about-card--full">
          <h3 className="about-card__title">
            Grounds {filledGrounds.length > 0 ? `(${filledGrounds.length})` : ''}
          </h3>
          <div className="about-card__body">
            {filledGrounds.length === 0 ? (
              <div className="league-preview-empty">No grounds added</div>
            ) : (
              <ul className="league-preview-grounds-list">
                {filledGrounds.map((g, idx) => (
                  <li key={g.localId} className="league-preview-ground-item">
                    <div className="league-preview-ground-item__index">{idx + 1}</div>
                    <div className="league-preview-ground-item__body">
                      <div className="league-preview-ground-item__name">
                        {g.groundName || 'Unnamed ground'}
                      </div>
                      {g.groundAddress && (
                        <div className="league-preview-ground-item__addr">{g.groundAddress}</div>
                      )}
                      <div className="league-preview-ground-item__meta">
                        <span>
                          <strong>Days:</strong> {buildDaysLabel(g.daysOpen)}
                        </span>
                        <span>
                          <strong>Hours:</strong> {buildHoursLabel(g.fromTime, g.toTime)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <div className="create-league-step-actions">
        <button
          type="button"
          className="create-league-btn-outline"
          onClick={onPrevious}
          disabled={isSubmitting}
        >
          PREVIOUS
        </button>
        <button
          type="button"
          className="create-league-btn-primary"
          onClick={() => void onCreate()}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating…' : 'CREATE'}
        </button>
      </div>
    </div>
  );
};

export default LeaguePreviewStep;
